package gr.tech.meetinggenie.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaGripVertical
import com.varabyte.kobweb.silk.components.icons.fa.FaPlus
import com.varabyte.kobweb.silk.components.icons.fa.FaTrash
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.meetinggenie.styles.*
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

/**
 * Reusable drag-and-drop data table shell used by WinsTableInput,
 * LossesTableInput, and ActiveProjectsTableInput.
 *
 * The drag handle (first column) and delete button (last column) are
 * provided automatically. Callers supply only the domain-specific
 * [headers] (Th cells) and [rowCells] (Td cells for a given index).
 *
 * @param rowCount       Current number of rows.
 * @param onMove         Called with (from, to) when a row is dragged to a new position.
 * @param onDelete       Called with the row index to remove.
 * @param maxRows        When [rowCount] reaches this value the add button is hidden.
 * @param addButtonText  Label on the add-row button.
 * @param onAdd          Called when the add button is clicked; pass null to hide the button entirely.
 * @param headers        Composable that emits the middle Th elements.
 * @param rowCells       Composable that emits the middle Td elements for [index].
 */
@Composable
fun DraggableDataTable(
    rowCount: Int,
    onMove: (from: Int, to: Int) -> Unit,
    onDelete: (index: Int) -> Unit,
    maxRows: Int = Int.MAX_VALUE,
    addButtonText: String = "Add Row",
    onAdd: (() -> Unit)? = null,
    headers: @Composable () -> Unit,
    rowCells: @Composable (index: Int) -> Unit,
) {
    var draggedIndex  by remember { mutableStateOf(-1) }
    var dragOverIndex by remember { mutableStateOf(-1) }

    Table(attrs = DataEntryTableStyle.toAttrs()) {
        Tr {
            Th(DataEntryControlCellStyle.toAttrs()) {}
            headers()
            Th(DataEntryControlCellStyle.toAttrs()) {}
        }

        for (index in 0 until rowCount) {
            Tr(
                attrs = DataEntryRowStyle.toModifier()
                    .then(
                        if (dragOverIndex == index && dragOverIndex != draggedIndex)
                            Modifier.borderTop(3.px, LineStyle.Solid, Theme.MediumGreen.rgb)
                        else Modifier
                    )
                    .onDragOver { it.preventDefault(); dragOverIndex = index }
                    .onDragLeave { if (dragOverIndex == index) dragOverIndex = -1 }
                    .onDrop {
                        if (draggedIndex != -1 && draggedIndex != dragOverIndex) {
                            val to = if (dragOverIndex > draggedIndex) dragOverIndex - 1 else dragOverIndex
                            onMove(draggedIndex, to)
                        }
                        draggedIndex = -1; dragOverIndex = -1
                    }
                    .toAttrs()
            ) {
                // Fixed first column: drag handle
                Td(
                    attrs = DataEntryControlCellStyle.toModifier()
                        .draggable(true)
                        .onDragStart { draggedIndex = index }
                        .toAttrs()
                ) {
                    FaGripVertical(modifier = DragHandleStyle.toModifier())
                }

                // Caller-provided cells
                rowCells(index)

                // Fixed last column: delete button
                Td(DataEntryControlCellStyle.toAttrs()) {
                    Button(
                        attrs = DeleteRowButtonStyle.toModifier()
                            .onClick { onDelete(index) }
                            .toAttrs()
                    ) {
                        FaTrash(size = IconSize.XS)
                    }
                }
            }
        }
    }

    if (onAdd != null && rowCount < maxRows) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Start,
        ) {
            Button(
                attrs = AddRowButtonStyle.toModifier()
                    .onClick { onAdd() }
                    .toAttrs()
            ) {
                FaPlus(modifier = Modifier.margin(right = 6.px))
                Text(addButtonText)
            }
        }
    }
}
